import React, { useState } from "react";
import { Bookmark, BookmarkCheck, Trash2, Pencil, Check, X, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { generateAITag } from "../lib/ai";

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
}

export default function Block({ block, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.content);
  const [draftCaption, setDraftCaption] = useState(block.caption || "");
  const [generatingTag, setGeneratingTag] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [customTagVal, setCustomTagVal] = useState("");
  const touchTimer = React.useRef(null);

  const bm = block.bookmark ? { key: block.bookmark, color: "#888888" } : null;

  async function setBookmark(key) {
    await onUpdate(block.id, { bookmark: key === block.bookmark ? "" : key });
  }

  async function saveEdit() {
    if (block.type === "text") {
      const v = draft.trim();
      if (!v) return;
      await onUpdate(block.id, { content: v });
    } else {
      await onUpdate(block.id, { caption: draftCaption.trim() });
    }
    setEditing(false);
  }

  function cancelEdit() {
    setDraft(block.content);
    setDraftCaption(block.caption || "");
    setEditing(false);
  }

  async function handleAITag() {
    setGeneratingTag(true);
    const tag = await generateAITag(block.content || block.caption);
    setGeneratingTag(false);
    if (tag) {
      await onUpdate(block.id, { bookmark: tag });
    }
  }

  function handleCustomTag() {
    setCustomTagVal("");
    setTagDialogOpen(true);
  }

  function submitCustomTag(e) {
    e.preventDefault();
    if (customTagVal.trim()) {
      onUpdate(block.id, { bookmark: customTagVal.trim() });
    }
    setTagDialogOpen(false);
  }

  function handleTouchStart() {
    touchTimer.current = setTimeout(() => setShowActions(true), 500);
  }

  function handleTouchEnd() {
    if (touchTimer.current) clearTimeout(touchTimer.current);
  }

  function handleTouchMove() {
    if (touchTimer.current) clearTimeout(touchTimer.current);
  }

  return (
    <article
      className="thought-enter group relative py-2"
      data-testid={`block-${block.id}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      <div className={`flex items-center justify-between mb-3 transition-opacity ${showActions ? "opacity-100" : "opacity-0 md:opacity-0 md:group-hover:opacity-100"}`}>
        <div className="flex items-center gap-2">
          <time className="meta-label text-muted-foreground" data-testid={`block-${block.id}-time`}>
            {formatTime(block.created_at)}
          </time>
          {bm && (
            <span
              className="meta-label flex items-center gap-1"
              style={{ color: bm.color }}
              data-testid={`block-${block.id}-bookmark-tag`}
            >
              <BookmarkCheck className="w-3 h-3" /> {bm.key}
            </span>
          )}
        </div>

        <div className="flex items-center gap-0.5">
          {!editing && (block.type === "text" || block.type === "image") && (
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 rounded hover:bg-secondary transition-colors"
              aria-label="edit"
              data-testid={`block-${block.id}-edit-button`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={generatingTag}
                className={`p-1.5 rounded hover:bg-secondary transition-colors ${generatingTag ? "opacity-50 animate-pulse" : ""}`}
                aria-label="tag"
                data-testid={`block-${block.id}-bookmark-button`}
              >
                {bm ? (
                  <BookmarkCheck className="w-3.5 h-3.5" style={{ color: bm.color }} />
                ) : (
                  <Bookmark className="w-3.5 h-3.5" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="meta-label text-muted-foreground">Tag this thought</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleAITag} className="meta-label cursor-pointer flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-blue-400" /> Auto-tag with AI
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCustomTag} className="meta-label cursor-pointer flex items-center gap-2">
                <Pencil className="w-3.5 h-3.5 text-muted-foreground" /> Custom Tag...
              </DropdownMenuItem>
              
              {bm && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setBookmark("")} className="meta-label cursor-pointer flex items-center gap-2 text-red-400">
                    <X className="w-3.5 h-3.5" /> Remove tag "{bm.key}"
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => onDelete(block.id)}
            className="p-1.5 rounded hover:bg-secondary transition-colors text-red-400/80 hover:text-red-400"
            aria-label="delete"
            data-testid={`block-${block.id}-delete-button`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="max-w-sm p-5">
          <DialogTitle className="meta-label">Custom Tag</DialogTitle>
          <DialogDescription className="sr-only">Enter a custom tag for this thought</DialogDescription>
          <form onSubmit={submitCustomTag} className="flex gap-2 mt-2">
            <Input 
              autoFocus 
              placeholder="e.g. Ideas" 
              value={customTagVal} 
              onChange={e => setCustomTagVal(e.target.value)} 
            />
            <Button type="submit">Tag</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Block body */}
      {block.type === "text" && !editing && (
        <p
          className="font-serif-journal whitespace-pre-wrap text-foreground text-lg sm:text-xl leading-relaxed"
          data-testid={`block-${block.id}-text`}
        >
          {block.content}
        </p>
      )}

      {block.type === "text" && editing && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(10, draft.split("\n").length + 1)}
            className="w-full bg-transparent border border-border rounded-md p-3 font-serif-journal text-lg leading-relaxed resize-y focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid={`block-${block.id}-edit-textarea`}
          />
          <div className="flex items-center gap-2 justify-end">
            <button onClick={cancelEdit} className="meta-label px-3 py-1.5 rounded hover:bg-secondary">
              <X className="w-3 h-3 inline mr-1" /> cancel
            </button>
            <button onClick={saveEdit} className="meta-label px-3 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90"
              data-testid={`block-${block.id}-save-button`}>
              <Check className="w-3 h-3 inline mr-1" /> save
            </button>
          </div>
        </div>
      )}

      {block.type === "image" && (
        <figure>
          <img
            src={block.content}
            alt={block.caption || ""}
            className="rounded-lg w-full max-h-[70vh] object-cover"
            data-testid={`block-${block.id}-image`}
          />
          {!editing && block.caption && (
            <figcaption className="mt-3 font-serif-journal italic text-base text-muted-foreground">
              {block.caption}
            </figcaption>
          )}
          {editing && (
            <div className="mt-3 space-y-2">
              <input
                value={draftCaption}
                onChange={(e) => setDraftCaption(e.target.value)}
                placeholder="caption…"
                className="w-full bg-transparent border border-border rounded-md p-2 font-serif-journal italic text-base focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <div className="flex items-center gap-2 justify-end">
                <button onClick={cancelEdit} className="meta-label px-3 py-1.5 rounded hover:bg-secondary">
                  cancel
                </button>
                <button onClick={saveEdit} className="meta-label px-3 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90">
                  save
                </button>
              </div>
            </div>
          )}
        </figure>
      )}

      {block.type === "drawing" && (
        <div className="rounded-lg border border-border p-2 bg-card/40">
          <img
            src={block.content}
            alt="drawing"
            className="w-full"
            data-testid={`block-${block.id}-drawing`}
          />
        </div>
      )}
    </article>
  );
}
